import type { FreeWorkoutItem } from './freeWorkoutBuilder';
type GroupType = 'superset' | 'circuit';
const ungroup = (item: FreeWorkoutItem): FreeWorkoutItem => {
  const result = { ...item }; delete result.groupId; delete result.groupType; return result;
};
/** Reordering/removal can split a block. Keep only contiguous groups with at least two members. */
export function normalizeRoutineGroups(items: readonly FreeWorkoutItem[]): FreeWorkoutItem[] {
  const output = items.map(ungroup), seen = new Set<string>();
  for (let index = 0; index < items.length;) {
    const item = items[index];
    if (!item.groupId) { index++; continue; }
    const groupType = item.groupType ?? 'superset';
    let end = index + 1;
    while (end < items.length && items[end].groupId === item.groupId && (items[end].groupType ?? 'superset') === groupType) end++;
    if (end - index >= 2) {
      const candidate = seen.has(item.groupId) ? item.groupId.slice(0, 55) + '-' + index : item.groupId;
      let groupId = candidate, serial = 1;
      while (seen.has(groupId)) groupId = candidate + '-' + serial++;
      seen.add(groupId);
      for (let member = index; member < end; member++) output[member] = { ...output[member], groupId, groupType };
    }
    index = end;
  }
  return output;
}
/** Linking extends the previous contiguous block; the remaining blocks are normalized independently. */
export function linkRoutineExercise(items: readonly FreeWorkoutItem[], index: number, type: GroupType | null): FreeWorkoutItem[] {
  if (!Number.isInteger(index) || index <= 0 || index >= items.length) return normalizeRoutineGroups(items);
  const next = items.map(item => ({ ...item }));
  if (!type) { next[index] = ungroup(next[index]); return normalizeRoutineGroups(next); }
  const previous = items[index - 1];
  let start = index - 1;
  if (previous.groupId) while (start > 0 && items[start - 1].groupId === previous.groupId && (items[start - 1].groupType ?? 'superset') === (previous.groupType ?? 'superset')) start--;
  const groupId = previous.groupId ?? 'block-' + index;
  for (let member = start; member <= index; member++) next[member] = { ...next[member], groupId, groupType: type };
  return normalizeRoutineGroups(next);
}
